import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import { getColorStyles } from '@risksmart-app/components/src/utils/colours';
import { type Ref, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDashboardWidgetSettings } from 'src/context/useDashboardWidgetSettings';
import type { PolicyRegisterFields } from 'src/pages/policy/types';

import { useGetWidgetData } from '../../gigawidget/hooks/useGetWidgetData';
import type { WidgetRef } from '../../types';
import { dataSources } from '../../universal-widget/data-sources';
import type { FilterSettings } from '../../universal-widget/util';
import { useWidgetSettingsHandler } from '../../widget-context/useWidgetSettingsHandler';
import LineChartWidget from '../line-chart-widget/LineChartWidget';
import { WidgetSettingsModal } from '../widget-settings-modal/WidgetSettingsModal';
import { resultHistoryWindowFilter } from './resultHistoryWindow';
import { sortByDateX } from './utils';
import { withFullResultHistory } from './withFullResultHistory';

/**
 * COI-838 — this chart PLOTS the nested rating series, so it opts out of the
 * newest-2 cap every other caller of `useGetPolicyRegister` now gets. Bound at
 * MODULE SCOPE so the wrapped `useWidgetQuery` identity is stable across
 * renders, and composed here rather than registered in `dataSources` — that map
 * builds the user-facing source picker.
 */
const dataSource = withFullResultHistory(dataSources.document);

export const DocumentRatingsOverTime = (
  _props: unknown,
  ref: Ref<WidgetRef>
) => {
  const [showModal, setShowModal] = useState(false);
  const [settings, setSettings] =
    useDashboardWidgetSettings<FilterSettings<'document'>>();
  const { t } = useTranslation(['common'], {
    keyPrefix: 'dashboard.widgets.documentRatingsOverTime',
  });

  useWidgetSettingsHandler(ref, () => setShowModal(true));

  const {
    tableProps: { allItems },
    loading,
  } = useGetWidgetData({
    dataSource,
    propertyFilterQuery: settings?.filtering,
    // COI-837 — restores the nested clip this widget lost in COI-600, now on
    // BOTH legs. `resultHistoryWindow` clips the nested assessment-result
    // series; it deliberately does not narrow the document rows, matching the
    // `documentAssessmentResultsWhere` this replaces.
    seriesDateFilterOptions: { dateFilter: resultHistoryWindowFilter },
  });

  const series = useDocumentRatingsOverTime(allItems);

  const { getByValue: getPerformanceByValue } = useRating('performance_result');

  const handleSave = async (data: FilterSettings) => {
    setSettings(data);
  };

  return (
    <>
      {showModal && (
        <WidgetSettingsModal
          onDismiss={() => setShowModal(false)}
          onSave={handleSave}
          dataSource={dataSource}
        />
      )}
      <LineChartWidget
        series={series}
        isLoading={loading}
        xTitle={t('xTitle')}
        yTitle={t('yTitle')}
        tooltipYValueFormatter={(y) =>
          getPerformanceByValue(y)?.label || String(y)
        }
        pointColorFormatter={(y) =>
          getColorStyles(getPerformanceByValue(y)?.color)?.backgroundColor
        }
      />
    </>
  );
};

const useDocumentRatingsOverTime = (
  data: readonly PolicyRegisterFields[] | undefined
) => {
  const series = data
    ? data.flatMap((document) => {
        if (
          !document.assessmentResults ||
          document.assessmentResults.length === 0
        ) {
          return [];
        }

        return {
          name: document.Title,
          data: document.assessmentResults
            .filter((result) => Boolean(result.documentAssessmentResult))
            .map((result) => ({
              x:
                result.documentAssessmentResult!.TestDate ??
                result.documentAssessmentResult!.CreatedAtTimestamp,
              y: result.documentAssessmentResult!.Rating ?? 0,
            }))
            .sort(sortByDateX),
        };
      })
    : [];

  return series;
};
